const clean = (value) => String(value ?? "").trim();

export const getVoucherValue = (winner) => clean(
  winner?.voucherCode ?? winner?.voucher_code ?? winner?.voucher_id ?? winner?.id_voucher
);

const findOutlet = (promo, winner) => {
  const outlets = Array.isArray(promo?.outlet) ? promo.outlet : [];
  return outlets.find((outlet) =>
    (winner?.outlet_type_id && clean(outlet.id) === clean(winner.outlet_type_id)) ||
    (winner?.store && clean(outlet.name).toLowerCase() === clean(winner.store).toLowerCase())
  );
};

export const normalizeRecipients = (winners = [], promo) => winners.map((winner, index) => {
  const outlet = findOutlet(promo, winner);
  return {
    ...winner,
    no: winner?.no ?? index + 1,
    winnerId: winner?.winnerId ?? winner?.id,
    fullName: clean(winner?.fullName ?? winner?.full_name) || "-",
    phoneNumber: clean(winner?.phoneNumber ?? winner?.phone_number),
    voucherCode: getVoucherValue(winner),
    storeName: clean(winner?.storeName ?? winner?.store ?? outlet?.name) || "Tanpa toko",
    voucherImage: winner?.voucherImage ?? winner?.voucher_image ?? outlet?.voucher_image ?? promo?.voucher_image ?? "",
    status: winner?.status,
  };
});

export const createBroadcastPreviewModel = (winners = [], promo) => {
  const normalized = normalizeRecipients(winners, promo);
  const excludedDisqualifiedCount = normalized.filter((winner) => Number(winner.status) === 2).length;
  const eligibleRecipients = normalized.filter((winner) => Number(winner.status) !== 2);
  const missingPhoneCount = eligibleRecipients.filter((winner) => !winner.phoneNumber).length;
  const missingVoucherCount = eligibleRecipients.filter((winner) => !winner.voucherCode).length;
  const missingImageCount = eligibleRecipients.filter((winner) => !winner.voucherImage).length;

  const voucherCounts = eligibleRecipients.reduce((counts, winner) => {
    if (!winner.voucherCode) return counts;
    const key = winner.voucherCode.toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
  const duplicateVoucherCount = Object.values(voucherCounts).filter((count) => count > 1).length;

  const outletSummary = Object.values(eligibleRecipients.reduce((summary, winner) => {
    const key = winner.storeName.toLowerCase();
    summary[key] = summary[key] || { name: winner.storeName, count: 0 };
    summary[key].count += 1;
    return summary;
  }, {})).sort((a, b) => a.name.localeCompare(b.name));

  const blockers = [];
  if (!eligibleRecipients.length) blockers.push("Tidak ada pemenang yang dapat menerima broadcast.");
  if (missingPhoneCount) blockers.push(`${missingPhoneCount} pemenang belum memiliki nomor WhatsApp.`);
  if (missingVoucherCount) blockers.push(`${missingVoucherCount} pemenang belum memiliki voucher.`);
  if (duplicateVoucherCount) blockers.push(`${duplicateVoucherCount} kode voucher digunakan lebih dari satu kali.`);

  const warnings = [];
  if (missingImageCount) warnings.push(`${missingImageCount} pemenang belum memiliki foto voucher.`);

  const messageSample = eligibleRecipients[0] || null;
  return {
    promo,
    eligibleRecipients,
    excludedDisqualifiedCount,
    outletSummary,
    blockers,
    warnings,
    messageSample,
    completeness: [
      { key: "phone", label: "Nomor WhatsApp", message: missingPhoneCount ? `${missingPhoneCount} belum tersedia` : "Lengkap", isComplete: missingPhoneCount === 0 },
      {
        key: "voucher",
        label: "Voucher",
        message: missingVoucherCount
          ? `${missingVoucherCount} belum tersedia`
          : duplicateVoucherCount
            ? `${duplicateVoucherCount} kode duplikat`
            : "Lengkap",
        isComplete: missingVoucherCount === 0 && duplicateVoucherCount === 0,
      },
      { key: "voucher-image", label: "Foto voucher", message: missingImageCount ? `${missingImageCount} belum tersedia` : "Lengkap", isComplete: missingImageCount === 0, warningOnly: true },
    ],
  };
};

export const buildBroadcastMessagePreview = (recipient, promo) => {
  if (!recipient) return "Tidak ada contoh penerima yang tersedia.";
  return `Selamat anda telah terpilih menjadi pemenang promo undian CPI bulan ${promo?.name || ""}.

Terima kasih atas kontribusi Anda dalam program Undian Promo Charoen Pokphand Indonesia. Dengan ikut serta dalam program ini, Anda telah mendukung kami untuk dapat memberikan pelayanan yang lebih baik lagi bagi Anda sebagai pelanggan setia PT Charoen Pokphand Indonesia`;
};

export const buildWinnerFormData = (winners = [], promotionId) => {
  const formData = new FormData();
  winners.forEach((winner) => {
    formData.append("winner_id[]", winner.winnerId ?? winner.id);
    formData.append("voucher_id[]", getVoucherValue(winner));
    formData.append("promotion_id[]", winner.promotion_id ?? promotionId);
    if (winner.outlet_type_id !== undefined && winner.outlet_type_id !== null) {
      formData.append("outlet_type_id[]", winner.outlet_type_id);
    }
    if (winner.upload_voucher) {
      formData.append("voucher_image[]", winner.upload_voucher);
    }
  });
  return formData;
};

export const getRequestErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;
